import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useApp } from '../store/AppContext';

type Creds = { username: string; password: string };





function stripLoginParams(): Creds | null {
  try {
    const q = new URLSearchParams(window.location.search);
    if (!q.has('username') && !q.has('password')) return null;
    const username = (q.get('username') || '').trim();
    const password = q.get('password') || '';
    q.delete('username');
    q.delete('password');
    const rest = q.toString();
    const path = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
    window.history.replaceState(null, '', path);
    return username && password ? { username, password } : null;
  } catch {
    return null;
  }
}

let urlCreds: Creds | null = stripLoginParams();
let urlLoginTried = false;

export function LoginPage() {
  const { login } = useApp();
  const [username, setUsername] = useState(urlCreds?.username ?? '');
  const [password, setPassword] = useState(urlCreds?.password ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const attempt = async (u: string, p: string) => {
    if (!u || !p) {
      setError('Введите имя пользователя и пароль, чтобы войти.');
      return;
    }
    setBusy(true);
    try {
      await login(u, p);
      urlCreds = null;
    } catch (e) {
      if (alive.current) setError((e instanceof Error && e.message) || 'Не удалось войти. Проверьте имя пользователя и пароль и попробуйте ещё раз.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  useEffect(() => {
    if (!urlCreds || urlLoginTried) return;
    urlLoginTried = true;
    void attempt(urlCreds.username, urlCreds.password);
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void attempt(username.trim(), password);
  };

  return (
    <section id="login">
      <div className="seam" aria-hidden="true" />
      <div className="auth">
        <div className="brand auth-brand">
          <span className="ms-mark ms-lg" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
          <div className="brand-name">
            BingCore
          </div>
        </div>

        <div className="card">
          <h1>Вход в панель</h1>

          {error !== null && (
            <div className="alert" id="loginError" role="alert">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ flex: 'none', marginTop: 1 }}
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16.5v.01" />
              </svg>
              <div id="loginErrorText">
                <b>{error}</b>
              </div>
            </div>
          )}

          <form id="loginForm" method="post" action="#" autoComplete="off" noValidate onSubmit={onSubmit}>
            <div className="field-row">
              <label htmlFor="lg">Имя пользователя</label>
              <input
                type="text"
                id="lg"
                autoComplete="username"
                spellCheck={false}
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="field-row">
              <label htmlFor="lp">Пароль</label>
              <input
                type="password"
                id="lp"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block mt-8" type="submit" id="loginSubmit" disabled={busy}>
              <span id="loginBtnLabel">{busy ? 'Вход...' : 'Войти'}</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
        </div>

        <div className="auth-foot">BingCore Control Center</div>
      </div>
    </section>
  );
}
